import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Search } from "lucide-react";

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => { api.get("/admin/customers").then((r) => setItems(r.data)); }, []);

  const open = async (uid) => {
    const { data } = await api.get(`/admin/customers/${uid}`);
    setDetail(data);
  };

  const filtered = items.filter((u) => !filter ||
    u.email.toLowerCase().includes(filter.toLowerCase()) ||
    u.name?.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="admin-customers">
      <div className="flex items-end justify-between border-b border-border pb-4 mb-6">
        <div>
          <h1 className="font-display text-4xl tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground italic mt-1">{items.length} registered reader{items.length === 1 ? "" : "s"}.</p>
        </div>
        <div className="flex items-center gap-2 bg-muted/60 rounded-sm px-3 py-1.5 border border-border w-72">
          <Search className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search…" className="flex-1 bg-transparent outline-none text-sm" data-testid="customers-search" />
        </div>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
            <tr><th className="px-4 py-3">Name</th><th>Email</th><th>Orders</th><th>Lifetime value</th><th>Joined</th></tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.user_id} className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40" onClick={() => open(u.user_id)} data-testid={`cust-row-${u.user_id}`}>
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td>{u.email}</td>
                <td>{u.orders_count}</td>
                <td>A${u.lifetime_value.toFixed(2)}</td>
                <td className="text-xs text-muted-foreground">{u.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground italic">No customers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <Dialog open onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm" data-testid="cust-detail">
            <DialogHeader>
              <DialogTitle className="font-display text-3xl tracking-tight">{detail.user.name}</DialogTitle>
              <div className="text-sm text-muted-foreground">{detail.user.email}</div>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-3 my-4">
              <div className="border border-border rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Orders</div><div className="text-2xl font-display mt-1">{detail.orders.length}</div></div>
              <div className="border border-border rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Library</div><div className="text-2xl font-display mt-1">{detail.library_count}</div></div>
              <div className="border border-border rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Roles</div><div className="text-sm mt-2 flex gap-1">{detail.user.roles?.map((r) => <Badge key={r} variant="outline" className="rounded-sm capitalize">{r}</Badge>)}</div></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Recent orders</div>
              {detail.orders.length === 0 ? <div className="text-sm text-muted-foreground italic">No orders.</div> : (
                <div className="space-y-2">
                  {detail.orders.slice(0, 10).map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-sm border-b border-border pb-2">
                      <div className="font-mono text-xs">{o.id.slice(0, 14)}…</div>
                      <div className="text-xs text-muted-foreground">{o.created_at?.slice(0, 10)}</div>
                      <Badge variant="outline" className="rounded-sm capitalize">{o.status}</Badge>
                      <div className="font-semibold">{o.currency === "usd" ? "US$" : "A$"}{o.total.toFixed(2)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {detail.contact && (
              <div className="mt-6 border-t border-border pt-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">CRM contact</div>
                <div className="text-sm">{detail.contact.tags?.map((t) => <Badge key={t} variant="outline" className="rounded-sm mr-1">{t}</Badge>)}</div>
                {detail.contact.notes && <div className="text-sm text-muted-foreground mt-2 italic">{detail.contact.notes}</div>}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
